import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import { Dialog, toast, useIsMobileScreenSize } from '@buffer-mono/popcorn'

import { graphql } from '../../../../gql'
import type {
  AttributionInput,
  DowngradeToFreeInput,
} from '../../../../gql/graphql'
import { useUser } from '../../../../common/context/User'
import { QUERY_ACCOUNT } from '../../../../common/graphql/account'
import { ORGANIZATION_ACTIONS } from '../../../../common/events/orgEvents'
import { isOnActiveTrial } from '../../../../common/utils/user'
import { prefixWithProduct } from '../../../../common/utils/prefixWithProduct'
import { GetPlanSelectorData } from '../../../PlanSelector/hooks/usePlanSelectorQuery'
import {
  ChannelsStep,
  FREE_PLAN_CHANNEL_LIMIT,
  getSelectableChannels,
} from './steps/ChannelsStep'
import { ConfirmStep } from './steps/ConfirmStep'
import {
  CANCEL_FLOW_ORG_ACTIVITY_SUMMARY,
  hasActivityStats,
  isDefaultActivityState,
  LossAversionStep,
} from './steps/LossAversionStep'
import { SuccessStep } from './steps/SuccessStep'
import { SurveyStep } from './steps/SurveyStep'
import type { SurveyResponse } from './steps/SurveyStep'

import styles from './index.module.css'

export const CANCEL_FLOW_DOWNGRADE_TO_FREE = graphql(/* GraphQL */ `
  mutation CancelFlowDowngradeToFree($input: DowngradeToFreeInput!) {
    downgradeToFree(input: $input) {
      __typename
      ... on UpdateBillingSubscriptionPlanResponse {
        status
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type Step = 'lossAversion' | 'channels' | 'survey' | 'confirm' | 'success'
type FlowStatus = 'idle' | 'submitting'

/**
 * `attribution` is required so every downgrade carries it — a frontend
 * precursor to making it required on the API.
 */
type DowngradeToFreeInputWithAttribution = Omit<
  DowngradeToFreeInput,
  'attribution'
> & {
  attribution: AttributionInput
}

const CancelFlowModal = ({
  dismissModal,
  isTrialCancellation,
}: {
  dismissModal: () => void
  isTrialCancellation?: boolean
}): JSX.Element => {
  const account = useUser()
  const organizationId = account?.currentOrganization?.id ?? ''
  const commonTrackingProperties =
    account?.currentOrganization?.commonTrackingProperties ?? null
  const wasOnTrial =
    isTrialCancellation ?? (account ? isOnActiveTrial(account) : false)

  const initialChannels = getSelectableChannels(
    account?.currentOrganization?.channels,
  )
  const needsChannelSelection = initialChannels.length > FREE_PLAN_CHANNEL_LIMIT
  const { data: activityData, loading: isLoadingStats } = useQuery(
    CANCEL_FLOW_ORG_ACTIVITY_SUMMARY,
    {
      variables: { organizationId },
      skip: !organizationId,
    },
  )
  const activitySummary = activityData?.organizationActivitySummary ?? null
  const hasLoadedActivityStats = hasActivityStats(activitySummary)
  const hasDefaultActivityState = isDefaultActivityState(activitySummary)
  const isLoadingInitialStats =
    isLoadingStats && !hasLoadedActivityStats && !hasDefaultActivityState
  const shouldShowLossAversion =
    isLoadingInitialStats || hasLoadedActivityStats || hasDefaultActivityState

  const steps: Step[] = [
    ...(shouldShowLossAversion ? (['lossAversion'] as const) : []),
    ...(needsChannelSelection ? (['channels'] as const) : []),
    'survey',
    'confirm',
    'success',
  ]

  const [step, setStep] = useState<Step>('lossAversion')
  const currentStep = steps.includes(step) ? step : steps[0]
  const cancellableSteps: Step[] = steps.filter(
    (flowStep) => flowStep !== 'success',
  )
  const currentStepIndex = cancellableSteps.indexOf(currentStep)
  const canGoBack = currentStepIndex > 0
  const stepLabel =
    currentStepIndex >= 0
      ? `Step ${currentStepIndex + 1} of ${cancellableSteps.length}`
      : undefined
  const [surveyResponse, setSurveyResponse] = useState<SurveyResponse | null>(
    null,
  )
  const [channelsToKeep, setChannelsToKeep] = useState<string[]>([])
  const [status, setStatus] = useState<FlowStatus>('idle')
  const [open, setOpen] = useState(true)
  const isSubmitting = status === 'submitting'
  const isMobile = useIsMobileScreenSize()
  const initialOrganizationId = useRef<string | null>(organizationId || null)

  const planName =
    account?.currentOrganization?.billing?.subscription?.plan?.name ?? ''
  const periodEnd =
    account?.currentOrganization?.billing?.subscription?.periodEnd ?? null
  const keptChannelCount = needsChannelSelection
    ? channelsToKeep.length
    : initialChannels.length
  const deactivatedChannelCount = Math.max(
    initialChannels.length - keptChannelCount,
    0,
  )

  const [downgradeToFree] = useMutation(CANCEL_FLOW_DOWNGRADE_TO_FREE, {
    refetchQueries: [
      { query: QUERY_ACCOUNT },
      {
        query: GetPlanSelectorData,
        variables: { input: { organizationId } },
      },
    ],
    awaitRefetchQueries: true,
  })

  const trackStepExited = (
    exitMethod: 'keepPlanButton' | 'modalClose' | 'backButton',
  ): void => {
    if (!organizationId) return
    if (currentStep === 'success') return
    BufferTracker.cancelFlowStepExited({
      organizationId,
      stepName: currentStep,
      exitMethod,
      reasonSelected: surveyResponse?.reason ?? null,
      ...commonTrackingProperties,
    })
  }

  const onOpenChange = (isOpen: boolean): void => {
    if (isOpen) return
    if (isSubmitting) return
    trackStepExited('modalClose')
    setOpen(false)
    dismissModal()
  }

  const handleDismiss = (): void => {
    if (isSubmitting) return
    trackStepExited('keepPlanButton')
    setOpen(false)
    dismissModal()
  }

  const goToNextStep = (): void => {
    const currentIndex = steps.indexOf(currentStep)
    const nextStep = steps[currentIndex + 1]
    if (nextStep) setStep(nextStep)
  }

  const goToPreviousStep = (): void => {
    const currentIndex = steps.indexOf(currentStep)
    const previousStep = steps[currentIndex - 1]
    if (previousStep) {
      trackStepExited('backButton')
      setStep(previousStep)
    }
  }

  const handleConfirm = async (): Promise<void> => {
    if (!organizationId) {
      toast.error(
        'Whoops! There was a problem cancelling your plan. Please try again or contact support.',
      )
      return
    }

    if (isSubmitting) return

    setStatus('submitting')

    try {
      BufferTracker.confirmCancellationCTAClicked({
        organizationId,
        ctaName: 'yesCancel_cancelFlow',
        ...commonTrackingProperties,
      })
      // Attribute the downgrade to the cancel flow so it isn't logged as
      // missing attribution downstream. There is no marketing upgrade path
      // here; the path name identifies the originating flow.
      const input: DowngradeToFreeInputWithAttribution = {
        organizationId,
        channelsToKeep,
        attribution: {
          cta: prefixWithProduct('cancelFlow-confirm-yesCancel-1'),
          upgradePathName: 'cancelFlow-downgrade',
        },
      }
      const { data: result } = await downgradeToFree({
        variables: { input },
      })

      const payload = result?.downgradeToFree
      if (payload?.__typename === 'VoidMutationError') {
        toast.error(payload.message)
        return
      }

      if (
        payload?.__typename !== 'UpdateBillingSubscriptionPlanResponse' ||
        payload.status !== 'success'
      ) {
        toast.error(
          'Whoops! There was a problem cancelling your plan. Please try again or contact support.',
        )
        return
      }

      BufferTracker.churnSurveyCompleted({
        organizationId,
        product: 'buffer',
        reason: surveyResponse?.reason ?? '',
        wasOnTrial,
        details:
          [surveyResponse?.details, surveyResponse?.otherDetails]
            .filter(Boolean)
            .join('\n\n') || undefined,
        ...commonTrackingProperties,
      })
      ORGANIZATION_ACTIONS.billingUpdated({ account })
      setStep('success')
    } catch {
      toast.error(
        'Whoops! There was a problem cancelling your plan. Please try again or contact support.',
      )
    } finally {
      setStatus('idle')
    }
  }

  useEffect(() => {
    if (!organizationId) return

    if (initialOrganizationId.current == null) {
      initialOrganizationId.current = organizationId
      return
    }

    if (initialOrganizationId.current !== organizationId) {
      setOpen(false)
      dismissModal()
    }
  }, [dismissModal, organizationId])

  useEffect(() => {
    if (!organizationId) return
    BufferTracker.cancelFlowStepViewed({
      organizationId,
      stepName: currentStep,
      ...commonTrackingProperties,
    })
  }, [currentStep, organizationId, commonTrackingProperties])

  const handleChannelContinue = (selectedChannelIds: string[]): void => {
    setChannelsToKeep(selectedChannelIds)
    goToNextStep()
  }

  const handleSurveyContinue = (response: SurveyResponse): void => {
    setSurveyResponse(response)
    goToNextStep()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        portal={true}
        size={
          currentStep === 'success' ? 'medium' : isMobile ? 'maximize' : 'large'
        }
        className={currentStep !== 'success' ? styles.modalContent : undefined}
      >
        {currentStep === 'lossAversion' && shouldShowLossAversion && (
          <LossAversionStep
            activitySummary={activitySummary}
            isLoadingStats={isLoadingStats}
            hasMoreSelectableChannelsThanFreePlan={needsChannelSelection}
            planName={planName}
            stepLabel={isLoadingInitialStats ? undefined : stepLabel}
            isTrialCancellation={wasOnTrial}
            onContinue={goToNextStep}
            onKeep={handleDismiss}
          />
        )}
        {currentStep === 'channels' && (
          <ChannelsStep
            key={organizationId}
            channels={initialChannels}
            initialSelectedChannelIds={channelsToKeep}
            stepLabel={stepLabel}
            isTrialCancellation={wasOnTrial}
            onBack={canGoBack ? goToPreviousStep : undefined}
            onDismiss={handleDismiss}
            onContinue={handleChannelContinue}
          />
        )}
        {currentStep === 'survey' && (
          <SurveyStep
            initialResponse={surveyResponse}
            isLoading={isSubmitting}
            stepLabel={stepLabel}
            isTrialCancellation={wasOnTrial}
            onBack={canGoBack ? goToPreviousStep : undefined}
            onDismiss={handleDismiss}
            onContinue={handleSurveyContinue}
          />
        )}
        {currentStep === 'confirm' && (
          <ConfirmStep
            planName={planName}
            periodEnd={periodEnd}
            isLoading={isSubmitting}
            isTrialCancellation={wasOnTrial}
            keptChannelCount={keptChannelCount}
            deactivatedChannelCount={deactivatedChannelCount}
            wasChannelSelectionShown={needsChannelSelection}
            stepLabel={stepLabel}
            onBack={canGoBack ? goToPreviousStep : undefined}
            onDismiss={handleDismiss}
            onConfirm={handleConfirm}
          />
        )}
        {currentStep === 'success' && (
          <SuccessStep
            periodEnd={periodEnd}
            isTrialCancellation={wasOnTrial}
            onDismiss={handleDismiss}
          />
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default CancelFlowModal
